/**
 * src/lib/hub/graph-style.ts — the PURE presentation rules for the entity graph.
 *
 * WHY THIS FILE EXISTS
 *   `EntityGraph.tsx` is a 3.5k-line canvas. The decisions that make the map MEAN
 *   something — which ontology family an edge belongs to, which way its arrow
 *   points, how confident it is, how big a node should be, and which nodes form
 *   the readable "ego core" a reader lands on — are pure data→style functions.
 *   They live here so they are unit-testable without a DOM and so the renderer
 *   stays a renderer. The arrow GEOMETRY lives here too (`edgeGeometry`), so the
 *   "the head lands on the ontology's `to` node" invariant is a test, not a hope.
 *
 * DISPLAY DOCTRINE (Lam ruling 2026-09-04, "typed-only + ego default")
 *   1. A TYPED edge is a claim we can defend; an untyped `related` co-occurrence
 *      is not. They must never look alike. Typed = scout-gold, confidence-alpha'd,
 *      arrowed in the ontology's stored direction. Inferred = thin grey dash, OFF
 *      by default.
 *   2. ONE accent. Gold is the relationship color; node type is carried by the
 *      RING, never by a second edge hue.
 *   3. Size = typed degree. Untyped links and asset satellites never inflate a
 *      node (the pre-2026-09 bug: a tag that merely co-occurred a lot drew as a
 *      hub).
 *   4. DIRECTION IS ONE SENTENCE. The arrow points at the ontology's `to` end and
 *      the on-canvas label is ALWAYS the stored-direction `forwardLabel`, so
 *      tail → label → head reads "Ted Yin —founded→ Ava Labs". Perspective labels
 *      (`inverseLabel`) belong only where the subject is NAMED — the inspector
 *      rows — never floating on a line whose arrow says the opposite.
 *   5. EVERY resting stroke clears WCAG 1.4.11 (3:1 non-text contrast) against
 *      the canvas ink. See `contrastOnInk` and its test.
 *
 * PURITY CONTRACT: no React, no DOM, no Firestore. Only `@shared/graph/ontology`
 * (itself dependency-free) for the canonical 9-type vocabulary.
 */

import { getTypeEntry, FORWARD, INVERSE } from '@shared/graph/ontology';

/* ── ontology families ───────────────────────────────────────────────────── */

/**
 * The three VISUAL families the 9-type ontology collapses into. The families are
 * a rendering concept, not a data concept — the stored vocabulary stays the 9 types.
 *
 *  - `spine`     the corporate/creative spine: a directed claim about who made,
 *                owns, employs or derives from whom. Solid + arrowhead.
 *  - `symmetric` a mutual claim with no direction (the ontology's `symmetric:true`
 *                types). Solid, NO arrowhead — an arrow here would be a lie.
 *  - `platform`  BUILT_ON: directed, but an infrastructure fact rather than a
 *                corporate one. Dashed + arrowhead so "runs on" reads apart from
 *                "is owned by" at a glance.
 */
export type EdgeFamily = 'spine' | 'symmetric' | 'platform';

/** Explicit family assignment for the platform type; everything else is derived. */
const PLATFORM_TYPES = new Set(['BUILT_ON']);

/**
 * The visual family of a relationship type. DERIVED from the ontology's own
 * `symmetric` flag (never a hand-kept mirror of it): symmetric types are
 * `symmetric`, BUILT_ON is `platform`, every other directed type is `spine`.
 * An unknown type degrades to `symmetric` — we will not draw an arrow for a
 * direction we cannot look up.
 */
export function edgeFamily(type: string | null | undefined): EdgeFamily {
  if (!type) return 'symmetric';
  const entry = getTypeEntry(type);
  if (!entry) return 'symmetric';
  if (entry.symmetric) return 'symmetric';
  return PLATFORM_TYPES.has(type) ? 'platform' : 'spine';
}

/** Perspective label for a type read FROM the `from` end (falls back to 'related to'). */
export function forwardLabelOf(type: string): string {
  return FORWARD[type] ?? 'related to';
}
/** Perspective label for a type read FROM the `to` end (falls back to 'related to'). */
export function inverseLabelOf(type: string): string {
  return INVERSE[type] ?? 'related to';
}

/* ── confidence ──────────────────────────────────────────────────────────── */

export type EdgeConfidence = 'high' | 'medium' | 'low';

/**
 * Confidence → stroke alpha. The whole point: a weak claim must LOOK weak.
 *
 * `low` is 0.5, not the 0.35 this shipped with: gold #E8A832 at 0.35 over the
 * #0D0D0D canvas composites to 2.09:1, under the WCAG 1.4.11 non-text minimum of
 * 3:1. At 0.5 it clears (3.09:1) and still reads as the faintest tier. Asserted
 * in graph-style.test.ts — do not lower these without re-running that test.
 */
export const CONFIDENCE_ALPHA: Record<EdgeConfidence, number> = {
  high: 0.9,
  medium: 0.6,
  low: 0.5,
};

/**
 * Coerce whatever the edge doc actually stored into a confidence TIER.
 *
 * `shared/graph/confidence.js`'s `effectiveConfidence` returns `edge.confidence`
 * verbatim when a human override exists, and five live Abstract edges store that
 * override as a NUMBER (0.95, 0.9) rather than a tier word. Unnormalised, those
 * fell through every `Record<EdgeConfidence, …>` lookup to the `low` default —
 * so the single most-corroborated edges on the map drew as the faintest, and the
 * inspector printed "0.95 CONFIDENCE". Numeric scores map ≥0.8 → high, ≥0.5 →
 * medium, else low; tier words pass through; anything unrecognisable is `low`
 * (we never round an unknown UP).
 *
 * This is the defensive half. The projection seam (`getChainTypedEdges`) also
 * normalises, so a numeric never reaches the client payload in the first place.
 */
export function normalizeConfidence(value: unknown): EdgeConfidence {
  if (value === 'high' || value === 'medium' || value === 'low') return value;
  const n = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
  if (Number.isFinite(n)) {
    if (n >= 0.8) return 'high';
    if (n >= 0.5) return 'medium';
    return 'low';
  }
  return 'low';
}

/** Alpha for a confidence value of any shape (tier word or numeric score). */
export function confidenceAlpha(c: unknown): number {
  return CONFIDENCE_ALPHA[normalizeConfidence(c)];
}

/** Sort rank — 0 is strongest. Used to pick a pair's DOMINANT edge. */
const CONFIDENCE_RANK: Record<EdgeConfidence, number> = { high: 0, medium: 1, low: 2 };
/** Rank for a confidence value of any shape (tier word or numeric score). */
export function confidenceRank(c: unknown): number {
  return CONFIDENCE_RANK[normalizeConfidence(c)];
}
const FAMILY_RANK: Record<EdgeFamily, number> = { spine: 0, platform: 1, symmetric: 2 };

/* ── edge style ──────────────────────────────────────────────────────────── */

/** The minimal shape `typedEdgeStyle` needs — a structural subset of ChainTypedEdge. */
export interface TypedEdgeLike {
  type: string;
  fromSlug: string;
  toSlug: string;
  /** Tier word, or a raw numeric score from a legacy human override. */
  confidence: string | number;
}

/** The resolved stroke recipe for ONE drawn link. */
export interface EdgeStyle {
  family: EdgeFamily;
  /** The type that won the pair (several typed edges can share one drawn link). */
  type: string;
  confidence: EdgeConfidence;
  /** Resting stroke alpha (confidence). */
  alpha: number;
  /** Resting stroke width in user units. */
  width: number;
  /** SVG stroke-dasharray, or null for a solid stroke. */
  dash: string | null;
  /**
   * Arrowhead placement relative to the DRAWN link's (source → target) order:
   *   'none'     symmetric — no arrow
   *   'forward'  the ontology direction runs source → target
   *   'backward' it runs target → source (the renderer swaps the line's ends and
   *              still uses a single markerEnd — no `auto-start-reverse` needed)
   */
  arrow: 'none' | 'forward' | 'backward';
  /**
   * The label to paint at the midpoint of a FOCUSED edge. ALWAYS the stored-
   * direction `forwardLabel` of the dominant type, so it reads as one sentence
   * along the arrow. Never a perspective label — see doctrine §4.
   */
  label: string;
}

/** The single accent. Typed edges are gold; nothing else on the canvas is. */
export const TYPED_EDGE_COLOR = '#E8A832'; // --amber / scout-gold
/**
 * Inferred (untyped `related`) edges: neutral grey, dashed, deliberately quiet —
 * but still ≥3:1 on the ink. `--slate` (#64748B) at 0.5 composites to only
 * 1.87:1, so this is a lightened slate that clears the bar (3.13:1) while
 * staying visually subordinate to the typed spine via its dash and hairline width.
 */
export const INFERRED_EDGE_COLOR = '#A9B6C7';
export const INFERRED_EDGE_DASH = '2 4';
export const INFERRED_EDGE_WIDTH = 0.9;
export const INFERRED_EDGE_ALPHA = 0.5;

/** The canvas ground every resting stroke is measured against (--ink). */
export const CANVAS_INK = '#0D0D0D';

const FAMILY_WIDTH: Record<EdgeFamily, number> = { spine: 1.8, platform: 1.5, symmetric: 1.3 };
const FAMILY_DASH: Record<EdgeFamily, string | null> = {
  spine: null,
  platform: '5 3',
  symmetric: null,
};

/**
 * Resolve the stroke recipe for a drawn link from the typed edge(s) on its pair.
 *
 * A pair may carry SEVERAL typed edges (Ava Labs ← founded ← Emin, and worked-at
 * too). The drawn line can only say one thing, so it says the STRUCTURAL one:
 * family rank first (spine ▸ platform ▸ symmetric), confidence as the tiebreak,
 * then type name for determinism. The inspector still lists every edge on the pair.
 *
 * @param edges       the typed edges sharing this pair (order-insensitive)
 * @param linkSource  the drawn link's `source` node id
 * @param linkTarget  the drawn link's `target` node id
 * @returns the recipe, or null when `edges` is empty (caller draws the inferred style)
 */
export function typedEdgeStyle(
  edges: readonly TypedEdgeLike[],
  linkSource: string,
  linkTarget: string,
): EdgeStyle | null {
  if (!edges || edges.length === 0) return null;
  const ranked = [...edges].sort((a, b) => {
    const fa = FAMILY_RANK[edgeFamily(a.type)];
    const fb = FAMILY_RANK[edgeFamily(b.type)];
    if (fa !== fb) return fa - fb;
    const ca = confidenceRank(a.confidence);
    const cb = confidenceRank(b.confidence);
    if (ca !== cb) return ca - cb;
    return a.type < b.type ? -1 : a.type > b.type ? 1 : 0;
  });
  const dom = ranked[0];
  const family = edgeFamily(dom.type);
  const confidence = normalizeConfidence(dom.confidence);
  let arrow: EdgeStyle['arrow'] = 'none';
  if (family !== 'symmetric') {
    // The arrow points at the ontology's `to` end. The drawn link's source/target
    // order comes from whichever typed edge claimed the pair FIRST, which is not
    // necessarily this (dominant) edge's direction — hence the explicit compare.
    if (dom.toSlug === linkTarget && dom.fromSlug === linkSource) arrow = 'forward';
    else if (dom.toSlug === linkSource && dom.fromSlug === linkTarget) arrow = 'backward';
    else arrow = 'none'; // endpoints don't match the drawn pair — never guess
  }
  return {
    family,
    type: dom.type,
    confidence,
    alpha: CONFIDENCE_ALPHA[confidence],
    width: FAMILY_WIDTH[family],
    dash: FAMILY_DASH[family],
    arrow,
    // STORED-DIRECTION label, always — it has to agree with the arrowhead.
    label: forwardLabelOf(dom.type),
  };
}

/* ── edge geometry (the arrow invariant) ─────────────────────────────────── */

export interface Pt {
  x: number;
  y: number;
}

/** Where the line is actually drawn, and whether it carries a head at (x2,y2). */
export interface EdgeGeometry {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  /** True when the renderer should attach `markerEnd` — the head sits at (x2,y2). */
  arrowed: boolean;
}

/** Gap between the arrowhead tip and the head node's rim, in user units. */
const ARROW_GAP = 3;

/**
 * Compute the drawn line for a link, with the arrowhead ALWAYS at (x2,y2).
 *
 * When the ontology direction runs against the drawn source→target order the line
 * is emitted TAIL-FIRST (ends swapped) so one `markerEnd` serves both directions —
 * no dependency on `orient="auto-start-reverse"`. The head end is then pulled back
 * to the head node's rim so the arrow lands on the circle instead of under the
 * avatar.
 *
 * THE INVARIANT this exists to make testable: for a directed type, (x2,y2) is
 * always within `radius + ARROW_GAP` of the ontology's `to` node, whichever way
 * the link happens to be stored. `graph-style.test.ts` asserts that for all nine
 * types, from geometry — so swapping the branch below fails the suite.
 */
export function edgeGeometry(
  style: Pick<EdgeStyle, 'arrow'> | null,
  sourcePos: Pt,
  targetPos: Pt,
  sourceRadius: number,
  targetRadius: number,
): EdgeGeometry {
  const arrow = style?.arrow ?? 'none';
  if (arrow === 'none') {
    return { x1: sourcePos.x, y1: sourcePos.y, x2: targetPos.x, y2: targetPos.y, arrowed: false };
  }
  // tail = the ontology `from` end, head = the ontology `to` end
  const tail = arrow === 'backward' ? targetPos : sourcePos;
  const head = arrow === 'backward' ? sourcePos : targetPos;
  const headR = arrow === 'backward' ? sourceRadius : targetRadius;
  const dx = head.x - tail.x;
  const dy = head.y - tail.y;
  const len = Math.hypot(dx, dy);
  const trim = headR + ARROW_GAP;
  if (!(len > trim + 2)) {
    // Too short to seat a head without eating the line — draw it plain.
    return { x1: tail.x, y1: tail.y, x2: head.x, y2: head.y, arrowed: false };
  }
  return {
    x1: tail.x,
    y1: tail.y,
    x2: tail.x + (dx / len) * (len - trim),
    y2: tail.y + (dy / len) * (len - trim),
    arrowed: true,
  };
}

/* ── contrast (WCAG 1.4.11) ──────────────────────────────────────────────── */

function srgbChannel(hex: string, i: number): number {
  const h = hex.replace('#', '');
  return parseInt(h.slice(i * 2, i * 2 + 2), 16) / 255;
}
function linearize(c: number): number {
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}
/** Relative luminance of a color composited over `bg` at `alpha`. */
function compositeLuminance(hex: string, alpha: number, bg: string): number {
  const chans = [0, 1, 2].map((i) => alpha * srgbChannel(hex, i) + (1 - alpha) * srgbChannel(bg, i));
  const [r, g, b] = chans.map(linearize);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
/**
 * WCAG contrast ratio of a stroke (color at `alpha`) against the canvas ink.
 * Non-text UI needs ≥ 3:1 (SC 1.4.11). Used by the palette test to pin the
 * confidence alphas and the inferred-edge grey to real, checkable numbers.
 */
export function contrastOnInk(hex: string, alpha: number, bg: string = CANVAS_INK): number {
  const l1 = compositeLuminance(hex, alpha, bg);
  const l2 = compositeLuminance(bg, 1, bg);
  const [hi, lo] = l1 >= l2 ? [l1, l2] : [l2, l1];
  return (hi + 0.05) / (lo + 0.05);
}

/* ── node size + type ring ───────────────────────────────────────────────── */

/** Radius floor — a zero-typed-degree member is still a real, tappable node. */
export const NODE_R_MIN = 12;
/** Radius ceiling — past this a hub eats its neighbours. */
export const NODE_R_MAX = 30;
const R_PER_TYPED_DEGREE = 2.8;
/** Coverage is a WHISPER, not a shout: log-scaled and capped at this many px. */
const COVERAGE_R_CAP = 3.5;

/**
 * Node radius from TYPED degree, plus a small coverage term.
 *
 * The pre-2026-09 formula used the node's DRAWN degree, so untyped `related`
 * co-occurrences and asset satellites inflated it — the map's biggest circles
 * were often its least-evidenced entities. Only confirmed typed edges count now;
 * `contentCount` (articles + beats) adds at most COVERAGE_R_CAP px so a
 * well-covered leaf is still distinguishable from an unwritten one.
 */
export function nodeRadius(typedDegree: number, coverageCount = 0): number {
  const deg = Math.max(0, typedDegree || 0);
  const cov = Math.min(COVERAGE_R_CAP, Math.log2(1 + Math.max(0, coverageCount || 0)));
  const r = NODE_R_MIN + deg * R_PER_TYPED_DEGREE + cov;
  return Math.round(Math.min(NODE_R_MAX, r) * 10) / 10;
}

/**
 * Entity-type ring colors. Before this, project / game / character all fell to
 * `var(--divider)` (#2A2A2A) — a hairline that reads as NOTHING, which meant the
 * bulk of every graph was undifferentiated avatar circles (audit 2026-09-04 §4).
 * These are low-saturation so they never compete with the gold edge accent.
 */
export const TYPE_RING: Record<string, string> = {
  person: '#8A9AB0', // --slate-light: the cool human tone (unchanged)
  organization: '#C9C7BE', // warm parchment grey (unchanged in spirit, now opaque)
  project: '#5F8C86', // muted teal
  game: '#8272A6', // muted violet
  character: '#A8697F', // muted rose
  token: '#64748B', // --slate
};
export const TYPE_RING_FALLBACK = '#64748B';

/**
 * Ring color for an entity type. `protocol` wears the CHAIN's identity color
 * (passed in) — a protocol node is the chain made visible.
 */
export function typeRingColor(type: string, protocolColor: string): string {
  if (type === 'protocol') return protocolColor;
  return TYPE_RING[type] ?? TYPE_RING_FALLBACK;
}

/* ── typed topology (built ONCE, consumed by every core pass) ────────────── */

/** Pair-deduped typed structure — the only degree/adjacency the display trusts. */
export interface TypedTopology<T extends TypedEdgeLike = TypedEdgeLike> {
  /** One entry per DRAWN pair, in first-seen order (the link source/target order). */
  pairs: { source: string; target: string }[];
  /** node id → typed PAIR degree (two edges between the same two nodes count once). */
  degree: Map<string, number>;
  /** Undirected 1-hop adjacency over typed pairs. */
  adjacency: Map<string, string[]>;
  /** node id → strongest confidence tier on any of its typed edges. */
  bestConfidence: Map<string, EdgeConfidence>;
  /** pairKey → every typed edge on that pair, in input order (payload type preserved). */
  byPair: Map<string, T[]>;
}

/** Undirected pair key — must match the renderer's. */
export function typedPairKey(a: string, b: string): string {
  return a < b ? `${a}|${b}` : `${b}|${a}`;
}

/**
 * Build the typed topology ONCE from a list of eligible typed edges.
 *
 * Both core passes (the drawn graph and the Core/All chip's count) read this same
 * structure, so the chip can never disagree with the canvas about what the core is.
 */
export function buildTypedTopology<T extends TypedEdgeLike>(
  edges: readonly T[],
): TypedTopology<T> {
  const pairs: { source: string; target: string }[] = [];
  const degree = new Map<string, number>();
  const adjacency = new Map<string, string[]>();
  const bestConfidence = new Map<string, EdgeConfidence>();
  const byPair = new Map<string, T[]>();
  const bumpConf = (id: string, c: EdgeConfidence) => {
    const cur = bestConfidence.get(id);
    if (!cur || confidenceRank(c) < confidenceRank(cur)) bestConfidence.set(id, c);
  };
  const link = (a: string, b: string) => {
    const list = adjacency.get(a);
    if (list) list.push(b);
    else adjacency.set(a, [b]);
  };
  for (const e of edges) {
    if (!e.fromSlug || !e.toSlug || e.fromSlug === e.toSlug) continue;
    const conf = normalizeConfidence(e.confidence);
    bumpConf(e.fromSlug, conf);
    bumpConf(e.toSlug, conf);
    const k = typedPairKey(e.fromSlug, e.toSlug);
    const existing = byPair.get(k);
    if (existing) {
      existing.push(e); // metadata only — the pair is already a drawn link
      continue;
    }
    byPair.set(k, [e]);
    pairs.push({ source: e.fromSlug, target: e.toSlug });
    degree.set(e.fromSlug, (degree.get(e.fromSlug) ?? 0) + 1);
    degree.set(e.toSlug, (degree.get(e.toSlug) ?? 0) + 1);
    link(e.fromSlug, e.toSlug);
    link(e.toSlug, e.fromSlug);
  }
  return { pairs, degree, adjacency, bestConfidence, byPair };
}

/**
 * Build a TYPED adjacency map from typed edges. Undirected (both directions
 * recorded) because "1 hop away" has no direction. Kept as a thin wrapper over
 * `buildTypedTopology` so callers that only need adjacency stay readable.
 */
export function typedAdjacency(
  edges: readonly { fromSlug: string; toSlug: string }[],
): Map<string, string[]> {
  return buildTypedTopology(
    edges.map((e) => ({ ...e, type: 'ASSOCIATED_WITH', confidence: 'low' as const })),
  ).adjacency;
}

/* ── ego core selection ──────────────────────────────────────────────────── */

/** The minimal node shape the ego selector needs. */
export interface EgoNode {
  id: string;
  /** Confirmed typed-edge degree. Untyped/asset links must NOT be counted here. */
  typedDegree: number;
  /** Coverage volume (articles + beats). Tie-break #2 — what we've written about. */
  contentCount?: number;
  /** Strongest confidence on any of this node's typed edges. Tie-break #3. */
  bestConfidence?: EdgeConfidence;
}

/** Default core seed count at a full-width desktop canvas. */
export const EGO_CORE_SEEDS = 20;
/** Floor for the seed count on the narrowest phone canvas. */
export const EGO_CORE_SEEDS_MIN = 8;

/**
 * Seed count scaled to the canvas WIDTH. A 20-seed core plus 1-hop neighbours is
 * legible at 787px; the same 20 seeds inside a 348px phone canvas is a smear.
 * ~45px of canvas per seed, clamped to [8, 20].
 */
export function egoSeedCount(canvasWidth: number): number {
  const w = Number.isFinite(canvasWidth) && canvasWidth > 0 ? canvasWidth : 787;
  return Math.max(EGO_CORE_SEEDS_MIN, Math.min(EGO_CORE_SEEDS, Math.round(w / 45)));
}

/** Max ALWAYS-ON rim labels in orbit mode, scaled the same way (~50px each). */
export function rimLabelCap(canvasWidth: number): number {
  const w = Number.isFinite(canvasWidth) && canvasWidth > 0 ? canvasWidth : 787;
  return Math.max(6, Math.min(15, Math.round(w / 50)));
}

/**
 * Rank ego seeds. Exported so the label pass can order candidates identically —
 * the nodes worth seeding are the nodes worth naming.
 *
 * typed degree desc ▸ coverage desc ▸ strongest edge confidence ▸ id asc.
 * Every tier is a real editorial signal and the last is a total order, so the
 * pick is deterministic (no Firestore/Map iteration order leaking into layout).
 */
export function compareEgoNodes(a: EgoNode, b: EgoNode): number {
  if (b.typedDegree !== a.typedDegree) return b.typedDegree - a.typedDegree;
  const ca = a.contentCount ?? 0;
  const cb = b.contentCount ?? 0;
  if (cb !== ca) return cb - ca;
  const ra = confidenceRank(a.bestConfidence ?? 'low');
  const rb = confidenceRank(b.bestConfidence ?? 'low');
  if (ra !== rb) return ra - rb;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/**
 * Pick the ego core: the connected spine a reader should LAND on.
 *
 * Abstract draws 152 nodes over 129 edges — a near-tree whose rim floaters
 * outnumber its structure and whose labels collide into mush. Landing on the
 * top-N typed-degree nodes plus their 1-hop neighbourhood turns that into a
 * readable core; everything else stays one click (or the directory) away.
 *
 * Rules:
 *   - seeds  = nodes with ≥1 TYPED edge, ranked by `compareEgoNodes`, capped at
 *              `seedCount`. Fewer than that? Every node with a typed edge is a seed.
 *   - core   = seeds ∪ their 1-hop typed neighbours.
 *   - `pinned` ids (a ?focus= deep link, an expanded node, the current selection)
 *              are ALWAYS admitted, along with their own 1-hop neighbours — that
 *              is what "clicking a node expands its neighbourhood" means.
 *   - a graph with NO typed edges at all returns every node: an empty canvas is
 *     worse than a hairball.
 *
 * Pure and order-independent. `adjacency` must be the TYPED adjacency only.
 */
export function selectEgoCore(
  nodes: readonly EgoNode[],
  adjacency: ReadonlyMap<string, readonly string[]>,
  seedCount: number = EGO_CORE_SEEDS,
  pinned: Iterable<string> = [],
): Set<string> {
  const core = new Set<string>();
  if (nodes.length === 0) return core;

  const connected = nodes.filter((n) => (n.typedDegree || 0) > 0);
  if (connected.length === 0) {
    // Nothing typed to build a core from — showing nothing would be a blank map.
    for (const n of nodes) core.add(n.id);
    return core;
  }

  const seeds = [...connected]
    .sort(compareEgoNodes)
    .slice(0, Math.max(1, seedCount))
    .map((n) => n.id);

  const known = new Set(nodes.map((n) => n.id));
  const expand = (id: string) => {
    if (!known.has(id)) return;
    core.add(id);
    for (const nb of adjacency.get(id) ?? []) {
      if (known.has(nb)) core.add(nb);
    }
  };
  for (const id of seeds) expand(id);
  for (const id of pinned) expand(id);
  return core;
}

/** The seed ids alone (no neighbourhood) — the label pass always names these. */
export function egoSeedIds(
  nodes: readonly EgoNode[],
  seedCount: number = EGO_CORE_SEEDS,
): Set<string> {
  return new Set(
    nodes
      .filter((n) => (n.typedDegree || 0) > 0)
      .sort(compareEgoNodes)
      .slice(0, Math.max(1, seedCount))
      .map((n) => n.id),
  );
}

/* ── resting labels ──────────────────────────────────────────────────────── */

/**
 * ~30px of canvas width per resting label; clamped so neither end smears.
 *
 * Only the DISCRETIONARY bucket is governed by this (see `restingLabelIds`) —
 * structure and seeds are named unconditionally. 30px/label puts a 787px canvas
 * at 26 names and a 348px phone canvas at 12, which measured clean on both.
 */
export function labelBudget(canvasWidth: number): number {
  const w = Number.isFinite(canvasWidth) && canvasWidth > 0 ? canvasWidth : 787;
  return Math.max(8, Math.min(40, Math.round(w / 30)));
}

/**
 * The nodes that carry their name AT REST (before hover/selection).
 *
 * The old rule labelled orbit-mode hubs and EVERY rim floater, which produced
 * Abstract's 159 colliding texts while leaving the core's own leaves anonymous —
 * a map of unnamed avatars. The rule now:
 *   1. every node with typedDegree ≥ 2 — it is structure, it gets a name
 *   2. every ego seed — it is what the reader landed on
 *   3. the remainder by typed degree (then the ego tie-breaks) until the
 *      width-scaled budget is spent
 * Rules 1 and 2 are unconditional; only rule 3 is capped, so a dense map degrades
 * by dropping its LEAST connected labels, never its spine.
 */
export function restingLabelIds(
  nodes: readonly EgoNode[],
  seeds: ReadonlySet<string>,
  budget: number,
): Set<string> {
  const out = new Set<string>();
  const rest: EgoNode[] = [];
  for (const n of nodes) {
    if ((n.typedDegree || 0) >= 2 || seeds.has(n.id)) out.add(n.id);
    else rest.push(n);
  }
  const room = budget - out.size;
  if (room > 0) {
    for (const n of [...rest].sort(compareEgoNodes).slice(0, room)) out.add(n.id);
  }
  return out;
}

/* ── provenance ──────────────────────────────────────────────────────────── */

/** Max distinct source hosts shipped per edge — payload discipline. */
export const SOURCE_HOSTS_CAP = 4;

/**
 * Host aliases — the reader wants the OUTLET, not the delivery mechanism.
 * `syndication.twitter.com` is how a post was fetched, not who published it.
 */
const HOST_ALIASES: Record<string, string> = {
  'twitter.com': 'x.com',
  'syndication.twitter.com': 'x.com',
  'mobile.twitter.com': 'x.com',
  'publish.twitter.com': 'x.com',
};

/**
 * HOSTNAMES ONLY, deduped, from a list of source urls.
 *
 * The evidence a reader needs is "who says so" — `avax.network`, `coindesk.com`.
 * The full url, the quoted snippet and the asserter stay server-side (they carry
 * scrape internals and model ids). `www.` and a leading `api.` are stripped and
 * the host lowercased, then Twitter's fetch hosts collapse to `x.com`, so one
 * outlet never reads as two or three corroborating sources.
 */
export function sourceHostsFromUrls(
  sources: readonly { url?: string | null }[] | null | undefined,
  cap: number = SOURCE_HOSTS_CAP,
): string[] {
  if (!Array.isArray(sources)) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const s of sources) {
    const url = s && typeof s === 'object' ? s.url : null;
    if (typeof url !== 'string' || url.length === 0) continue;
    let host: string;
    try {
      host = new URL(url).hostname.toLowerCase();
    } catch {
      continue; // not a parseable url — never guess at a host
    }
    host = host.replace(/^www\./, '').replace(/^api\./, '');
    host = HOST_ALIASES[host] ?? host;
    if (!host || seen.has(host)) continue;
    seen.add(host);
    out.push(host);
    if (out.length >= cap) break;
  }
  return out;
}
